"use client";

import { useMotionMode } from "@/context/MotionContext";
import { useThemeMode } from "@/context/ThemeContext";

// =============================================================================
// CRT OVERLAY
// =============================================================================
// Degraded space transmission aesthetic - scanlines + vignette
// Pure CSS, pointer-events: none, sits on top of everything
//
// GPU Tier behavior:
// T0: Nothing (reduced motion)
// T1: Scanlines only (static)
// T2: Scanlines + vignette (breathing)
// T3: Full (event-driven effects like scanline flicker)

interface CRTOverlayProps {
  /** Ring is currently spinning - increases scanline opacity */
  isSpinning?: boolean;
}

export function CRTOverlay({ isSpinning = false }: CRTOverlayProps) {
  const { gpuTier, reducedMotion } = useMotionMode();
  const { mode } = useThemeMode();
  const isLightMode = mode === "light";

  // T0 or reduced motion: no CRT effects
  if (reducedMotion || gpuTier === 0) return null;

  const showScanlines = gpuTier >= 1;
  const showVignette = gpuTier >= 2;
  const vignetteBreathes = gpuTier >= 2;

  // Scanline opacity: reduced in light mode (high contrast needs less opacity)
  // Dark mode: 0.1 normal, 0.18 spinning | Light mode: 0.05 normal, 0.08 spinning
  const baseOpacity = isLightMode ? 0.05 : 0.1;
  const spinOpacity = isLightMode ? 0.08 : 0.18;
  const scanlineOpacity = isSpinning && gpuTier >= 3 ? spinOpacity : baseOpacity;

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none"
      aria-hidden="true"
    >
      {/* Scanlines */}
      {showScanlines && (
        <div
          className="absolute inset-0 transition-opacity duration-150"
          style={{
            background: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(0, 0, 0, ${scanlineOpacity}) 2px,
              rgba(0, 0, 0, ${scanlineOpacity}) 4px
            )`,
          }}
        />
      )}

      {/* Vignette */}
      {showVignette && (
        <div
          className={vignetteBreathes ? "animate-vignette-breathe" : ""}
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(
              ellipse at center,
              transparent 0%,
              transparent 50%,
              rgba(0, 0, 0, 0.3) 100%
            )`,
            opacity: vignetteBreathes ? undefined : 0.4,
          }}
        />
      )}
    </div>
  );
}
